import { useEffect, useState } from 'react';
import api from '../api/axios';

interface Location { id: number; name: string; }
interface Inventory { id: number; name: string; purchase_price: number; jenis: 'bahan_baku' | 'produk_jadi'; }
interface PurchaseItem { inventory_id: number; quantity: number; price: number; }
interface Purchase {
  id: number; date: string; supplier_name: string; location_id: number;
  location_name: string; created_by: string; total: number; notes: string;
  item_names: string; item_count: number;
}
interface PurchaseDetail {
  id: number; date: string; supplier_name: string; total: number; notes: string;
  location: string; user: string;
  items: Array<{ inventory_id: number; inventory_name: string; quantity: number; price: number; }>;
}

const rupiahFormat = (n: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = { date: today(), supplier_name: '', location_id: '', notes: '' };
const emptyItem = (): PurchaseItem => ({ inventory_id: 0, quantity: 1, price: 0 });

export default function Pembelian() {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [inventories, setInventories] = useState<Inventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItem[]>([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<PurchaseDetail | null>(null);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    const [purRes, locRes, invRes] = await Promise.allSettled([
      api.get('/purchases/getAllPurchases'),
      api.get('/locations/getAllLocations'),
      api.get('/inventories/getAllInventories'),
    ]);
    if (purRes.status === 'fulfilled') setPurchases(purRes.value.data);
    if (locRes.status === 'fulfilled') setLocations(locRes.value.data);
    if (invRes.status === 'fulfilled') setInventories(invRes.value.data);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm);
    setPurchaseItems([emptyItem()]);
    setError('');
    setShowModal(true);
  };

  const openEdit = async (id: number) => {
    try {
      const res = await api.get(`/purchases/getPurchaseById/${id}`);
      const d = res.data;
      setEditId(id);
      setForm({
        date: d.date?.slice(0, 10) || today(),
        supplier_name: d.supplier_name,
        location_id: d.location_id?.toString() || '',
        notes: d.notes || '',
      });
      setPurchaseItems(d.items.map((i: any) => ({ inventory_id: i.inventory_id, quantity: i.quantity, price: i.price })));
      setError('');
      setShowModal(true);
    } catch {
      setError('Gagal memuat detail');
    }
  };

  const openDetail = async (id: number) => {
    try {
      const res = await api.get(`/purchases/getPurchaseById/${id}`);
      setDetail(res.data);
    } catch {
      setError('Gagal memuat detail');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (purchaseItems.some((i) => !i.inventory_id)) { setError('Pilih produk untuk setiap baris'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, location_id: Number(form.location_id), items: purchaseItems };
      if (editId) {
        await api.put(`/purchases/updatePurchase/${editId}`, payload);
      } else {
        await api.post('/purchases/createPurchase', payload);
      }
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (index: number, field: keyof PurchaseItem, value: number) => {
    const updated = [...purchaseItems];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'inventory_id') {
      const inv = inventories.find((i) => i.id === value);
      if (inv) updated[index].price = inv.purchase_price;
    }
    setPurchaseItems(updated);
  };

  const totalForm = purchaseItems.reduce((acc, i) => acc + i.quantity * i.price, 0);
  const filtered = purchases.filter(
    (p) => p.supplier_name?.toLowerCase().includes(search.toLowerCase()) || p.id?.toString().includes(search)
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pembelian</h1>
          <p className="text-gray-500 text-sm mt-1">{purchases.length} transaksi pembelian</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Tambah Pembelian
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input type="text" placeholder="Cari nama supplier atau ID..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-400">
            <svg className="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Memuat...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tanggal</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Supplier</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Lokasi</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Barang</th>
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                  <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-400">Belum ada transaksi pembelian</td></tr>
                ) : filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 text-gray-400 text-xs">#{p.id}</td>
                    <td className="px-5 py-4 text-gray-600">{new Date(p.date).toLocaleDateString('id-ID')}</td>
                    <td className="px-5 py-4 font-medium text-gray-800">{p.supplier_name}</td>
                    <td className="px-5 py-4 text-gray-600">{p.location_name || '-'}</td>
                    <td className="px-5 py-4 max-w-xs">
                      {p.item_names ? (
                        <div>
                          <p className="text-gray-700 text-xs truncate" title={p.item_names}>{p.item_names}</p>
                          <p className="text-gray-400 text-xs mt-0.5">{p.item_count} item</p>
                        </div>
                      ) : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-5 py-4 text-right font-semibold text-purple-600">{rupiahFormat(p.total)}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openDetail(p.id)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition" title="Detail">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                        <button onClick={() => openEdit(p.id)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition" title="Edit">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-lg font-semibold text-gray-800">{editId ? 'Edit Pembelian' : 'Tambah Pembelian'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-5">
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tanggal *</label>
                  <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lokasi *</label>
                  <select required value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">-- Pilih Lokasi --</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nama Supplier *</label>
                  <input required value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Nama supplier" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Catatan</label>
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Catatan (opsional)" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Item Produk</label>
                  <button type="button" onClick={() => setPurchaseItems([...purchaseItems, emptyItem()])}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Tambah Baris
                  </button>
                </div>
                <div className="space-y-2">
                  {purchaseItems.map((item, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <select value={item.inventory_id} onChange={(e) => updateItem(i, 'inventory_id', Number(e.target.value))}
                        className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                        <option value={0}>-- Pilih Bahan Baku --</option>
                        {inventories.filter((inv) => inv.jenis === 'bahan_baku').map((inv) => <option key={inv.id} value={inv.id}>{inv.name}</option>)}
                      </select>
                      <input type="number" min={1} value={item.quantity} onChange={(e) => updateItem(i, 'quantity', Number(e.target.value))}
                        className="w-20 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Qty" />
                      <input type="number" min={0} value={item.price} onChange={(e) => updateItem(i, 'price', Number(e.target.value))}
                        className="w-32 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Harga" />
                      <span className="text-xs text-gray-500 w-28 text-right">{rupiahFormat(item.quantity * item.price)}</span>
                      {purchaseItems.length > 1 && (
                        <button type="button" onClick={() => setPurchaseItems(purchaseItems.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-3 pt-3 border-t border-gray-100">
                  <span className="text-sm font-semibold text-gray-800">Total: {rupiahFormat(totalForm)}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Batal</button>
                <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition">
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-lg font-semibold text-gray-800">Detail Pembelian #{detail.id}</h2>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {[
                  { label: 'Tanggal', value: new Date(detail.date).toLocaleDateString('id-ID', { dateStyle: 'long' }) },
                  { label: 'Supplier', value: detail.supplier_name },
                  { label: 'Lokasi', value: detail.location || '-' },
                  { label: 'Dibuat oleh', value: detail.user || '-' },
                  { label: 'Catatan', value: detail.notes || '-' },
                ].map((row) => (
                  <div key={row.label} className={row.label === 'Catatan' ? 'col-span-2' : ''}>
                    <p className="text-xs text-gray-400 mb-0.5">{row.label}</p>
                    <p className="font-medium text-gray-800">{row.value}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Item Produk</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400">
                      <th className="text-left pb-2">Produk</th>
                      <th className="text-right pb-2">Qty</th>
                      <th className="text-right pb-2">Harga</th>
                      <th className="text-right pb-2">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {detail.items.map((item, i) => (
                      <tr key={i}>
                        <td className="py-2 text-gray-700">{item.inventory_name}</td>
                        <td className="py-2 text-right text-gray-600">{item.quantity}</td>
                        <td className="py-2 text-right text-gray-600">{rupiahFormat(item.price)}</td>
                        <td className="py-2 text-right font-medium">{rupiahFormat(item.quantity * item.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200">
                      <td colSpan={3} className="pt-3 font-semibold text-gray-700">Total</td>
                      <td className="pt-3 text-right font-bold text-purple-600">{rupiahFormat(detail.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
